import { useMemo, useState } from "react";
import { Card, Table, Tag, Switch, Select, Button, Space, Empty, Tooltip } from "antd";
import Swal from "sweetalert2";
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useUpdateUserFeature } from "../../../hooks/useUserProfile";

// =============================================================================
// PlanFeatures
//
// Props:
//   allFeatures    — full feature master list from fetchFeatures API (array)
//   userFeatureIds — userData.features e.g. "1,2,5,6,16,20" (comma-separated string)
//   userId         — required for the enable/disable calls
//   isLoading      — boolean
//
// Each row has an Enable/Disable toggle that calls
// POST /subscription/update-user-feature { user_id, feature_id, action }.
// "Add Feature" enables one of the master features the user doesn't have yet.
// =============================================================================
const PlanFeatures = ({
  allFeatures = [],
  userFeatureIds = "",
  userId,
  isLoading = false,
}) => {
  const updateFeature = useUpdateUserFeature();

  // Which feature row is mid-request — keeps the spinner on that switch only
  const [pendingId, setPendingId] = useState(null);
  const [featureToAdd, setFeatureToAdd] = useState(null);

  // Parse "1,2,5,6,16,20" → Set{1,2,5,6,16,20}
  const enabledIds = useMemo(
    () =>
      new Set(
        userFeatureIds
          .toString()
          .split(",")
          .map((id) => parseInt(id.trim(), 10))
          .filter(Boolean),
      ),
    [userFeatureIds],
  );

  // Rows = features the user currently has, sorted by feature_order
  const featuresWithStatus = useMemo(
    () =>
      [...allFeatures]
        .filter((f) => enabledIds.has(f.id))
        .sort((a, b) => (a.feature_order ?? 0) - (b.feature_order ?? 0))
        .map((f) => ({ ...f, is_enabled: true })),
    [allFeatures, enabledIds],
  );

  // Master features not yet on this user — the "Add Feature" options
  const availableToAdd = useMemo(
    () =>
      [...allFeatures]
        .filter((f) => !enabledIds.has(f.id))
        .sort((a, b) => (a.feature_order ?? 0) - (b.feature_order ?? 0)),
    [allFeatures, enabledIds],
  );

  const enabledCount = featuresWithStatus.length;

  // ── Toggle handler ─────────────────────────────────────────────────────────
  const runUpdate = (featureId, action) => {
    if (!userId) {
      Swal.fire("Error", "User not loaded yet. Please try again.", "error");
      return;
    }
    setPendingId(featureId);
    updateFeature.mutate(
      { userId, featureId, action },
      {
        onSettled: () => setPendingId(null),
        onSuccess: () => {
          if (action === "enable") setFeatureToAdd(null);
        },
      },
    );
  };

  const handleToggle = (record, checked) => {
    if (checked) {
      runUpdate(record.id, "enable");
      return;
    }
    // Disabling removes the feature from the user's plan — confirm first
    Swal.fire({
      title: "Disable this feature?",
      html: `<strong>${record.name}</strong> will be removed from this user's plan.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Disable",
      confirmButtonColor: "#dc2626",
    }).then((r) => {
      if (r.isConfirmed) runUpdate(record.id, "disable");
    });
  };

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "#",
      key: "idx",
      width: 50,
      align: "center",
      render: (_, __, i) => (
        <span style={{ color: "#8c8c8c", fontWeight: 600 }}>{i + 1}</span>
      ),
    },
    {
      title: "Feature",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div>
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: record.is_enabled ? "#262626" : "#bfbfbf",
            }}
          >
            {text}
          </div>
          {record.feature_key !== "tm_document_size" && (
            <>
              <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>
                {record.description}
              </div>
              {record.feature_type === "numeric" && record.is_enabled && (
                <div style={{ fontSize: 11, color: "#1677ff", marginTop: 2, fontWeight: 600 }}>
                  Default limit: {record.default_value}
                </div>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_enabled",
      key: "is_enabled",
      align: "center",
      width: 130,
      render: (enabled) =>
        enabled ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            ENABLED
          </Tag>
        ) : (
          <Tag
            icon={<CloseCircleOutlined />}
            color="default"
            style={{ opacity: 0.5 }}
          >
            DISABLED
          </Tag>
        ),
    },
    {
      title: "Enable / Disable",
      key: "toggle",
      align: "center",
      width: 130,
      render: (_, record) => (
        <Tooltip
          title={record.is_enabled ? "Disable this feature" : "Enable this feature"}
        >
          <Switch
            checked={!!record.is_enabled}
            loading={pendingId === record.id}
            disabled={updateFeature.isPending && pendingId !== record.id}
            onChange={(checked) => handleToggle(record, checked)}
            checkedChildren="On"
            unCheckedChildren="Off"
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Card
      title={
        <div className="d-flex align-items-center gap-2">
          <AppstoreOutlined style={{ color: "#1677ff", fontSize: 18 }} />
          <span style={{ fontWeight: 600 }}>Plan Features</span>
          <Tag color="blue" style={{ marginLeft: 4 }}>
            {featuresWithStatus.length}
          </Tag>
        </div>
      }
      extra={
        availableToAdd.length > 0 && (
          <Space>
            <Select
              placeholder="Select a feature to enable"
              value={featureToAdd}
              onChange={setFeatureToAdd}
              style={{ minWidth: 240 }}
              showSearch
              optionFilterProp="label"
              allowClear
              options={availableToAdd.map((f) => ({
                value: f.id,
                label: f.name,
              }))}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!featureToAdd}
              loading={updateFeature.isPending && pendingId === featureToAdd}
              onClick={() => runUpdate(featureToAdd, "enable")}
            >
              Add Feature
            </Button>
          </Space>
        )
      }
      className="shadow-sm mb-4"
      style={{ borderRadius: 12 }}
    >
      {featuresWithStatus.length > 0 ? (
        <>
          <Table
            columns={columns}
            dataSource={featuresWithStatus.map((f) => ({ ...f, key: f.id }))}
            pagination={false}
            size="middle"
            bordered
            loading={isLoading}
            rowClassName={(record) => (!record.is_enabled ? "feature-row-disabled" : "")}
          />

          {/* Stats bar */}
          <div
            className="mt-3 p-3 rounded"
            style={{
              background: "linear-gradient(135deg,#f0f7ff 0%,#e8f4fd 100%)",
              border: "1px solid #bae0ff",
            }}
          >
            <div className="row text-center g-0">
              <div className="col-6">
                <div style={{ fontSize: 22, fontWeight: 700, color: "#52c41a" }}>
                  {enabledCount}
                </div>
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>Active Features</div>
              </div>
              <div className="col-6">
                <div style={{ fontSize: 22, fontWeight: 700, color: "#8c8c8c" }}>
                  {availableToAdd.length}
                </div>
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>Available to Add</div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-5">
          <AppstoreOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />
          <p className="text-muted mt-3">
            {isLoading
              ? "Loading features…"
              : "No features assigned to this user. Use “Add Feature” above to enable one."}
          </p>
          {!isLoading && availableToAdd.length === 0 && (
            <Empty description="No master features available" />
          )}
        </div>
      )}

      <style>{`
        .feature-row-disabled td {
          opacity: 0.45;
        }
      `}</style>
    </Card>
  );
};

export default PlanFeatures;
